import { useState, useEffect } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Check, X, ChevronDown, ChevronUp, Clock } from 'lucide-react'
import { appointmentApi } from '@services/appointment.service'
import { Button } from '@components/ui/button'
import { Input } from '@components/ui/input'
import { Label } from '@components/ui/label'
import { Textarea } from '@components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@components/ui/select'
import { Card, CardContent, CardHeader, CardTitle } from '@components/ui/card'
import { Badge } from '@components/ui/badge'
import { useToast } from '@hooks/use-toast'
import { formatClinicDate, formatClinicTime, DEFAULT_CLINIC_TZ } from '@lib/clinicDate'
import { Appointment } from '@/types'

const appointmentTypeLabels: Record<string, string> = {
  consultation: 'Consulta',
  follow_up: 'Seguimiento',
  emergency: 'Emergencia',
  routine_checkup: 'Revisión Rutinaria',
  vaccination: 'Vacunación',
  lab_test: 'Laboratorio',
  imaging: 'Imagen',
  procedure: 'Procedimiento',
  telemedicine: 'Telemedicina',
}

// All scheduling is reckoned in the clinic timezone.
const CLINIC_TZ = 'America/Mexico_City'
// Machine format (YYYY-MM-DD) — used for <input type="date"> values and slot API params.
const clinicDate = (iso: string) =>
  new Intl.DateTimeFormat('en-CA', { timeZone: CLINIC_TZ }).format(new Date(iso))
const clinicToday = () =>
  new Intl.DateTimeFormat('en-CA', { timeZone: CLINIC_TZ }).format(new Date())
const addDays = (ymd: string, days: number) => {
  const [y, m, d] = ymd.split('-').map(Number)
  const dt = new Date(Date.UTC(y, m - 1, d))
  dt.setUTCDate(dt.getUTCDate() + days)
  return dt.toISOString().slice(0, 10)
}
const sameInstant = (a: string, b: string) => new Date(a).getTime() === new Date(b).getTime()

interface PendingCardProps {
  appointment: Appointment
  onResolved: () => void
}

function PendingCard({ appointment, onResolved }: PendingCardProps) {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [showRejectForm, setShowRejectForm] = useState(false)
  const [reason, setReason] = useState('')

  // Date/time the doctor will meet the patient. Defaults to the requested slot.
  const [slotDate, setSlotDate] = useState(() => clinicDate(appointment.appointment_date))
  const [slots, setSlots] = useState<{ start_at: string; end_at: string }[]>([])
  const [selected, setSelected] = useState<string>(appointment.appointment_date)
  const [slotsLoading, setSlotsLoading] = useState(false)
  // The clinical-availability region drives how the date is formatted.
  const [regionTz, setRegionTz] = useState<string>(DEFAULT_CLINIC_TZ)

  // Load available slots for the chosen day (excluding this appointment so its
  // own requested slot is offered).
  useEffect(() => {
    let cancelled = false
    setSlotsLoading(true)
    appointmentApi
      .getAvailableSlots(appointment.doctor_id, slotDate, slotDate, appointment.id)
      .then((res) => {
        if (cancelled) return
        setSlots(res.slots)
        setRegionTz(res.timezone || DEFAULT_CLINIC_TZ)
        setSelected((prev) => {
          const keep = res.slots.find((s) => prev && sameInstant(s.start_at, prev))
          if (keep) return keep.start_at
          const own = res.slots.find((s) => sameInstant(s.start_at, appointment.appointment_date))
          return own ? own.start_at : ''
        })
      })
      .catch(() => {
        if (!cancelled) setSlots([])
      })
      .finally(() => {
        if (!cancelled) setSlotsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [slotDate, appointment.doctor_id, appointment.id, appointment.appointment_date])

  const minDate = clinicToday()
  const maxDate = addDays(minDate, 90)

  const acceptMutation = useMutation({
    mutationFn: () => {
      // Default to the originally requested slot when the doctor hasn't chosen a
      // different time (or no alternative slots are available for that day). This
      // keeps Accept usable even when the doctor has no open availability — in
      // that case we simply approve the slot the patient asked for.
      const target = selected || appointment.appointment_date
      if (sameInstant(target, appointment.appointment_date)) {
        return appointmentApi.approveAppointment(appointment.id)
      }
      return appointmentApi.updateAppointment(appointment.id, {
        appointment_date: target,
        status: 'scheduled',
      })
    },
    onSuccess: () => {
      toast({ title: 'Cita aceptada', description: `${appointment.appointment_number} ha sido programada.` })
      queryClient.invalidateQueries({ queryKey: ['pending-approvals'] })
      queryClient.invalidateQueries({ queryKey: ['appointments'] })
      onResolved()
    },
    onError: () => {
      toast({ title: 'Error', description: 'No se pudo aceptar la cita.', variant: 'destructive' })
    },
  })

  const rejectMutation = useMutation({
    mutationFn: () => appointmentApi.rejectAppointment(appointment.id, { rejection_reason: reason || undefined }),
    onSuccess: () => {
      toast({ title: 'Cita rechazada', description: `${appointment.appointment_number} fue rechazada.` })
      queryClient.invalidateQueries({ queryKey: ['pending-approvals'] })
      queryClient.invalidateQueries({ queryKey: ['appointments'] })
      onResolved()
    },
    onError: () => {
      toast({ title: 'Error', description: 'No se pudo rechazar la cita.', variant: 'destructive' })
    },
  })

  const isPending = acceptMutation.isPending || rejectMutation.isPending

  return (
    <div className="rounded-lg border bg-card p-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-sm">{appointment.patient_name ?? appointment.patient_id}</span>
            <Badge variant="outline" className="text-xs">
              {appointmentTypeLabels[appointment.appointment_type] ?? appointment.appointment_type}
            </Badge>
          </div>
          <p className="text-xs text-muted-foreground flex items-center gap-1">
            <Clock className="h-3 w-3" />
            Solicitada: {formatClinicTime(appointment.appointment_date, regionTz)} · {formatClinicDate(appointment.appointment_date, regionTz)}
          </p>
          <p className="text-sm text-muted-foreground line-clamp-2">{appointment.reason}</p>
        </div>
      </div>

      {/* Date & time to meet */}
      {!showRejectForm && (
        <div className="grid grid-cols-2 gap-3 rounded-md bg-muted/40 p-3">
          <div className="space-y-1">
            <Label className="text-xs">Fecha de la cita</Label>
            <Input
              type="date"
              lang="es-MX"
              min={minDate}
              max={maxDate}
              value={slotDate}
              onChange={(e) => setSlotDate(e.target.value)}
              className="h-8 text-sm"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Hora de la cita</Label>
            <Select value={selected} onValueChange={setSelected} disabled={slotsLoading || slots.length === 0}>
              <SelectTrigger className="h-8 text-sm">
                <SelectValue placeholder={slotsLoading ? 'Cargando...' : 'Seleccionar hora'} />
              </SelectTrigger>
              <SelectContent>
                {slots.map((s) => (
                  <SelectItem key={s.start_at} value={s.start_at}>
                    {formatClinicTime(s.start_at, regionTz)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!slotsLoading && slots.length === 0 && (
              <p className="text-[11px] text-muted-foreground">
                Sin horarios para esta fecha. Al aceptar se usará la hora solicitada ({formatClinicTime(appointment.appointment_date, regionTz)}).
              </p>
            )}
          </div>
        </div>
      )}

      {!showRejectForm ? (
        <div className="flex gap-2">
          <Button
            size="sm"
            className="bg-teal-600 hover:bg-teal-700 text-white gap-1"
            onClick={() => acceptMutation.mutate()}
            disabled={isPending}
          >
            <Check className="h-3.5 w-3.5" />
            Aceptar
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="border-destructive text-destructive hover:bg-destructive/10 gap-1"
            onClick={() => setShowRejectForm(true)}
            disabled={isPending}
          >
            <X className="h-3.5 w-3.5" />
            Rechazar
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          <Textarea
            placeholder="Motivo de rechazo (opcional)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            className="text-sm resize-none"
          />
          <div className="flex gap-2">
            <Button size="sm" variant="destructive" onClick={() => rejectMutation.mutate()} disabled={isPending}>
              {rejectMutation.isPending ? 'Rechazando...' : 'Confirmar rechazo'}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setShowRejectForm(false)
                setReason('')
              }}
              disabled={isPending}
            >
              Cancelar
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

interface Props {
  doctorId?: string
  // Called after an appointment is approved/rejected so the parent can refresh
  // its own appointment list (which isn't part of this component's query).
  onResolved?: () => void
}

export function PendingApprovalsSection({ doctorId: _doctorId, onResolved }: Props) {
  const [collapsed, setCollapsed] = useState(false)

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['pending-approvals'],
    queryFn: () => appointmentApi.getPendingApprovals(),
    refetchInterval: 60_000,
  })

  const items = data?.items ?? []
  const total = data?.total ?? 0

  if (isLoading) return null
  if (total === 0) return null

  return (
    <Card className="border-2 border-blue-400 bg-blue-50">
      <CardHeader className="pb-3">
        <button
          type="button"
          className="flex items-center justify-between w-full text-left"
          onClick={() => setCollapsed((c) => !c)}
        >
          <CardTitle className="flex items-center gap-2 text-base text-blue-900">
            <Clock className="h-4 w-4" />
            Solicitudes pendientes
            <span className="rounded bg-yellow-300 px-2 py-0.5 text-xs font-semibold text-yellow-900">
              {total}
            </span>
          </CardTitle>
          {collapsed ? (
            <ChevronDown className="h-4 w-4 text-blue-700" />
          ) : (
            <ChevronUp className="h-4 w-4 text-blue-700" />
          )}
        </button>
      </CardHeader>
      {!collapsed && (
        <CardContent className="space-y-3">
          {items.map((appt) => (
            <PendingCard
              key={appt.id}
              appointment={appt}
              onResolved={() => {
                refetch()
                onResolved?.()
              }}
            />
          ))}
        </CardContent>
      )}
    </Card>
  )
}
